<template>
  <v-row no-gutters>
    <v-col>
      <ReportHeading
        label="Звіт по роботах"
        :loading="loading"
        class="no-print"
        @update-report="updateReport"
      >
        <template v-slot:action>
          <DownloadFile
            location="xlsx"
            :params="specificationData"
            filename="Специфікація"
            extension="xlsx"
            tooltip="Завантажити у форматі Excel"
          />
        </template>
        <template v-slot:filters>
          <v-row
            align="center"
            dense
          >
            <v-col cols="4">
              <DateRangePicker
                :start.sync="dateFrom"
                :end.sync="dateTo"
                :disabled="loading"
                :filled="false"
                @change="updateReport"
              />
            </v-col>
            <v-col cols="4">
              <ClientProjectSelect
                v-model="project"
                :current="true"
                label="Фільтр по проектам"
                dense
                placeholder="Оберіть проект"
                @input="onProjectSelect"
              />
            </v-col>
          </v-row>
        </template>
      </ReportHeading>

      <Totals
        class="no-print"
        :source="filteredIssues"
        @payedChange="updateTotalPayed"
        @spentChange="updateTotalSpent"
      />

      <PrintForm
        :items="issues"
        :update-total-payed="totalPayed"
        :update-total-spent="totalSpent"
        :unable-to-manage-logs="false"
        :filter="filter"
      />
    </v-col>
  </v-row>
</template>

<script>
import _ from "lodash";

import PrintForm from "@/views/reports/worklog/PrintForm.vue";
import Totals from "@/views/reports/worklog/Totals.vue";
import * as clientDashboardApi from "@/backend/clientDashboardApi.js";
import DownloadFile from "@/components/DowloadFile.vue";
import ReportHeading from "@/components/ReportHeading.vue";
import { columnTypes, numberToString } from "@/helpers";
import ClientProjectSelect from "@/components/selects/ClientProjectSelect";
import DateRangePicker from "@/components/story/DateRangePicker.vue";

export default {
  components: {
    PrintForm,
    Totals,
    DownloadFile,
    ReportHeading,
    DateRangePicker,
    ClientProjectSelect,
  },
  data: () => ({
    loading: false,
    issues: [],
    totalPayed: 0,
    totalSpent: 0,
    dateFrom: "",
    dateTo: "",
    project: {},
    labelProject: "Фільтр по проекту",
  }),
  computed: {
    filteredIssues() {
      return this.issues.sort((a, b) => b.numberedKey - a.numberedKey);
    },
    specificationData() {
      const report = {
        rows: this.issues.map((i) => {
          const payedHours =
            i.worklog.reduce((sum, n) => sum + n.payedSeconds, 0) / 3600;
          const spentHours =
            i.worklog.reduce((sum, n) => sum + n.timeSpentSeconds, 0) / 3600;
          return [
            i.key,
            i.summary,
            i.status,
            numberToString(spentHours.toFixed(2)),
            numberToString(payedHours.toFixed(2)),
            numberToString((payedHours * 500).toFixed(2)),
            numberToString((payedHours * 500 * 1.2).toFixed(2)),
          ];
        }),
        columns: [
          "Номер",
          "Завдання (зміст наданих послуг)",
          "Статус",
          "Години без урахування рівня співробітника",
          "Години з урахуванням рівня співробітника",
          "Вартість за базовим тарифом, без ПДВ",
          "Вартість за базовим тарифом, з ПДВ",
        ],
        columnTypes: [
          columnTypes.STRING,
          columnTypes.STRING,
          columnTypes.STRING,
          columnTypes.NUMBER,
          columnTypes.NUMBER,
          columnTypes.NUMBER,
          columnTypes.NUMBER,
        ],
      };
      const amountCostVat = report.rows.reduce(
        (accumulator, currentValue) => accumulator + +currentValue[6],
        0
      );
      const amountCost = report.rows.reduce(
        (accumulator, currentValue) => accumulator + +currentValue[5],
        0
      );

      const amountPayedHours = this.issues.reduce((total, i) => {
        const issueTotal = i.worklog.reduce((total, w) => {
          return total + w.payedHours;
        }, 0);
        return total + issueTotal;
      }, 0);

      const amountSpentHours = this.issues.reduce((total, i) => {
        const issueTotal = i.worklog.reduce((sum, w) => {
          return sum + w.timeSpentSeconds;
        }, 0);
        return total + issueTotal;
      }, 0) / 3600;

      report.rows.push([
        "",
        "",
        "Загалом:",
        numberToString(amountSpentHours.toFixed(2)),
        numberToString(amountPayedHours.toFixed(2)),
        numberToString(amountCost.toFixed(2)),
        numberToString(amountCostVat.toFixed(2)),
      ]);

      return report;
    },
    filterClean() {
      return !this.dateFrom && !this.dateTo && !this.project.id
      ;
    },
    filter() {
      return {
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        project: this.project,
      };
    },
  },
  watch: {
    filter() {
      if (this.filterClean){
        this.issues = [];
      } else {
        this.updateReport();
      }
    },
  },
  created() {
    this.dateTo = this.moment().format("YYYY-MM-DD");
    this.dateFrom = this.moment().subtract(9, "days").format("YYYY-MM-DD");
  },
  methods: {
    async updateReport() {
      this.loading = true;
      this.issues = [];

      const filterParams = {
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        projectId: this.project?.id || "",
      };

      try {
        const worklogs = await clientDashboardApi
          .worklogUpdateReport(filterParams);
        console.log(worklogs);
        const groupedByIssue = _.groupBy(worklogs, (w) => w.key);
        _.forIn(groupedByIssue, (g) => {
          this.issues.push({
            key: g[0].key,
            numberedKey: g[0].numberedKey,
            summary: g[0].summary,
            author: g[0].issueAuthor,
            status: g[0].status,
            dueDate: g[0].dueDate,
            description: g[0].description,
            customerDescription: g[0].customerDescription,
            worklog: g.map((i) => {
              return {
                employee: i.employee,
                comment: i.comment,
                date: i.date,
                payedSeconds: i.payedSeconds,
                timeSpentSeconds: i.timeSpentSeconds,
                payedHours: i.payedHours,
              };
            }),
          });
        });
      } catch {
        this.$notifyWarning();
      }
      this.loading = false;
    },
    updateTotalPayed(totalPayedHours) {
      this.totalPayed = totalPayedHours;
    },
    updateTotalSpent(totalTimeSpentHours) {
      this.totalSpent = totalTimeSpentHours;
    },
    onProjectSelect(project) {
      this.project = project;
    },
  },
};
</script>
